import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/api";
import { toast } from "sonner";
import { Download, Share2, MessageCircle } from "lucide-react";
import { WhatsAppSendDialog } from "@/components/WhatsAppSendDialog";

const LEVELS = [
  { level: 1, title: "Photo + details only", desc: "Quality, shade no., company and WSR — no stock shown" },
  { level: 2, title: "Details + than stock", desc: "Adds than stock under each shade" },
  { level: 3, title: "Details + than & meter", desc: "Shows both than and meter stock" },
  { level: 4, title: "Details + meter only", desc: "Shows meter stock, than hidden" },
];

export const ShareCatalogueDialog = ({ open, onOpenChange, ids }) => {
  const [level, setLevel] = useState(1);
  const [busy, setBusy] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [file, setFile] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const build = async () => {
    setBusy(true);
    try {
      const res = await api.post("/catalogue", { ids, level, format: "image" }, { responseType: "blob" });
      const f = new File([res.data], `shade-catalogue-${Date.now()}.jpg`, { type: "image/jpeg" });
      setFile(f);
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      setBlobUrl(URL.createObjectURL(f));
      toast.success("Catalogue ready");
    } catch (err) {
      let msg;
      if (err.response?.data instanceof Blob) {
        try {
          msg = formatApiErrorDetail(JSON.parse(await err.response.data.text()).detail);
        } catch {
          msg = "Could not build the catalogue";
        }
      } else msg = formatApiErrorDetail(err.response?.data?.detail);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const download = () => {
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = file.name;
    a.click();
  };

  const share = async () => {
    setPickerOpen(true);
  };

  const reset = (o) => {
    if (!o) {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      setBlobUrl(null);
      setFile(null);
    }
    onOpenChange(o);
  };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogContent data-testid="share-catalogue-dialog" className="max-h-[92vh] max-w-lg overflow-y-auto rounded-sm">
        <DialogHeader>
          <DialogTitle className="font-head text-xl font-extrabold tracking-tighter">
            Share catalogue
          </DialogTitle>
          <DialogDescription className="text-xs">
            {ids.length} shade{ids.length === 1 ? "" : "s"} will be placed on one image you can send to a customer.
          </DialogDescription>
        </DialogHeader>

        {!blobUrl ? (
          <>
            <div className="space-y-2">
              {LEVELS.map((o) => (
                <button
                  key={o.level}
                  type="button"
                  data-testid={`catalogue-level-${o.level}`}
                  onClick={() => setLevel(o.level)}
                  className={`w-full border p-3 text-left transition-colors duration-200 ${
                    level === o.level ? "border-zinc-950 bg-zinc-50" : "border-zinc-200 hover:border-zinc-400"
                  }`}
                >
                  <p className="text-sm font-semibold">{o.title}</p>
                  <p className="mt-0.5 text-xs text-zinc-500">{o.desc}</p>
                </button>
              ))}
            </div>
            <Button
              data-testid="catalogue-build-button"
              disabled={busy}
              className="w-full rounded-sm bg-[var(--mod-primary)] text-white hover:opacity-90"
              onClick={build}
            >
              {busy ? "Building catalogue…" : "Build catalogue image"}
            </Button>
          </>
        ) : (
          <>
            <img
              src={blobUrl}
              alt="Shade catalogue preview"
              data-testid="catalogue-preview"
              className="max-h-[46vh] w-full border border-zinc-200 object-contain"
            />
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                data-testid="catalogue-share-button"
                className="flex-1 rounded-sm bg-[#128C7E] text-white hover:bg-[#0f7568]"
                onClick={share}
              >
                <MessageCircle size={15} className="mr-1.5" /> Send on WhatsApp
              </Button>
              <Button
                data-testid="catalogue-download-button"
                variant="outline"
                className="flex-1 rounded-sm"
                onClick={download}
              >
                <Download size={15} className="mr-1.5" /> Save image
              </Button>
              <Button
                data-testid="catalogue-rebuild-button"
                variant="ghost"
                className="rounded-sm"
                onClick={() => {
                  URL.revokeObjectURL(blobUrl);
                  setBlobUrl(null);
                }}
              >
                <Share2 size={15} className="mr-1.5" /> Change
              </Button>
            </div>
          </>
        )}
      </DialogContent>
      <WhatsAppSendDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        title="Send catalogue on WhatsApp"
        file={file}
        fileLabel="catalogue"
        message={`Namaste, sharing our latest shade catalogue (${ids.length} shade${
          ids.length === 1 ? "" : "s"
        }). Please let us know your requirement.`}
      />
    </Dialog>
  );
};
